import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '../hooks/use-toast';
import { bhdToBase } from './CurrencyContext';
import { useSettings } from './SettingsContext';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  // Packaging fees (pouch / wooden VIP box) are admin-editable in BHD via
  // Admin > Settings, instead of being hardcoded — the cart always reflects
  // whatever price is currently configured there.
  const { settings } = useSettings();
  const packagingFeeFor = (packaging) => {
    if (packaging === 'wooden') return bhdToBase(Number(settings.packagingBoxPrice) || 0);
    if (packaging === 'pouch') return bhdToBase(Number(settings.packagingPouchPrice) || 0);
    return 0;
  };

  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem('hjr_cart');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [wishlist, setWishlist] = useState(() => {
    try {
      const stored = localStorage.getItem('hjr_wishlist');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Applied coupon lives here (not on the checkout page) so a code entered on the cart
  // page — where shoppers can preview the discount before committing to checkout —
  // carries straight through to checkout instead of having to be re-entered.
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const stored = localStorage.getItem('hjr_coupon');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem('hjr_cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem('hjr_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    if (appliedCoupon) localStorage.setItem('hjr_coupon', JSON.stringify(appliedCoupon));
    else localStorage.removeItem('hjr_coupon');
  }, [appliedCoupon]);

  const addToCart = (product, qty = 1, packaging = null) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        // Re-adding the same product updates its packaging choice (last selection wins)
        // rather than splitting into a second line, since carts/orders key by product id.
        return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty, packaging: packaging || null, packagingFee: packagingFeeFor(packaging) } : i));
      }
      return [...prev, { ...product, qty, packaging: packaging || null, packagingFee: packagingFeeFor(packaging) }];
    });
    toast({ title: 'تمت الإضافة إلى السلة / Added to cart', description: product.nameAr || product.nameEn, variant: 'success' });
  };

  const removeFromCart = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };

  const clearCart = () => {
    setItems([]);
    setAppliedCoupon(null);
  };

  const cartCount = items.reduce((sum, i) => sum + i.qty, 0);
  const cartTotal = items.reduce((sum, i) => sum + (i.price + (i.packagingFee || 0)) * i.qty, 0);

  const discountAmount = appliedCoupon
    ? Math.min(cartTotal, appliedCoupon.type === 'percent' ? cartTotal * (appliedCoupon.value / 100) : bhdToBase(appliedCoupon.value))
    : 0;

  const applyCoupon = (coupon) => setAppliedCoupon(coupon);
  const removeCoupon = () => setAppliedCoupon(null);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) return prev.filter((i) => i.id !== product.id);
      return [...prev, product];
    });
  };

  const isInWishlist = (id) => wishlist.some((i) => i.id === id);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        cartCount,
        cartTotal,
        wishlist,
        toggleWishlist,
        isInWishlist,
        appliedCoupon,
        applyCoupon,
        removeCoupon,
        discountAmount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
